import { CDMRecord } from '../../../Common/DataOwnerModels/CDMRecord';
import { CompiledExpression, RuleSetRecord } from '../../../Common/DataOwnerModels/RuleSetRecord';

export const EM_DASH = '\u2014';
export const ALL_FILTER_KEY = 'All';

export type ExpressionLanguage = 'cSharp' | 'python' | 'sql';

export const expressionLanguageLabels: Record<ExpressionLanguage, string> = {
  cSharp: 'C#',
  python: 'Python',
  sql: 'SQL',
};

export const displayValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return EM_DASH;
  }
  if (Array.isArray(value)) {
    const nonEmpty = value.filter((v) => v !== null && v !== undefined && String(v).trim() !== '');
    return nonEmpty.length > 0 ? nonEmpty.join(', ') : EM_DASH;
  }
  const text = String(value).trim();
  return text === '' ? EM_DASH : text;
};

export const getEnvironmentLabel = (environment?: number): string => {
  if (environment === 0) {
    return 'Non-Production';
  }
  if (environment === 1) {
    return 'Production';
  }
  return EM_DASH;
};

export const getExpressionText = (expression: CompiledExpression | undefined, language: ExpressionLanguage): string =>
  displayValue(expression?.[language]);

export const getRuleCount = (ruleSet: RuleSetRecord): number => ruleSet?.rules?.length ?? 0;

export const getRuleSetCountForCdm = (
  ruleSets: RuleSetRecord[] | null | undefined,
  cdmId: string,
  environment?: number
): number =>
  (ruleSets ?? []).filter(
    (ruleSet) =>
      ruleSet?.dataSource?.entityTypeId === cdmId &&
      (environment === undefined || ruleSet.environment === environment)
  ).length;

// Falls back to the raw entity id when the CDM is not in the data owner's approved CDM list.
export const getDataSourceEntityName = (
  ruleSet: RuleSetRecord,
  cdmList?: CDMRecord[] | null
): string => {
  const entityTypeId = ruleSet?.dataSource?.entityTypeId;
  if (!entityTypeId || entityTypeId.trim() === '') {
    return EM_DASH;
  }
  return displayValue(cdmList?.find((cdm) => cdm.id === entityTypeId)?.name ?? entityTypeId);
};

const getSortValue = (ruleSet: RuleSetRecord, columnKey: string, cdmList?: CDMRecord[] | null): string | number => {
  switch (columnKey) {
    case 'dataSourceEntity':
      return getDataSourceEntityName(ruleSet, cdmList).toLowerCase();
    case 'version':
      return ruleSet.version ?? -1;
    case 'environment':
      return ruleSet.environment ?? -1;
    case 'ruleCount':
      return getRuleCount(ruleSet);
    case 'tags':
      return (ruleSet.tags ?? []).join(', ').toLowerCase();
    default: {
      const value = (ruleSet as unknown as Record<string, unknown>)[columnKey];
      return value === null || value === undefined ? '' : String(value).toLowerCase();
    }
  }
};

export const sortRuleSets = (
  ruleSets: RuleSetRecord[],
  columnKey: string | undefined,
  isSortedDescending: boolean,
  cdmList?: CDMRecord[] | null
): RuleSetRecord[] => {
  if (!columnKey) {
    return ruleSets;
  }
  return [...ruleSets].sort((a, b) => {
    const aValue = getSortValue(a, columnKey, cdmList);
    const bValue = getSortValue(b, columnKey, cdmList);
    if (aValue === bValue) {
      return 0;
    }
    const result = aValue > bValue ? 1 : -1;
    return isSortedDescending ? -result : result;
  });
};

export type RuleSetFilter = {
  searchText?: string;
  environment?: string;
  status?: string;
  entityTypeId?: string;
};

export const filterRuleSets = (ruleSets: RuleSetRecord[], filter: RuleSetFilter): RuleSetRecord[] => {
  const searchText = filter.searchText?.trim().toLowerCase() ?? '';
  return ruleSets.filter((ruleSet) => {
    if (searchText) {
      const name = ruleSet.name?.toLowerCase() ?? '';
      const category = ruleSet.category?.toLowerCase() ?? '';
      if (!name.includes(searchText) && !category.includes(searchText)) {
        return false;
      }
    }
    if (filter.environment && filter.environment !== ALL_FILTER_KEY && String(ruleSet.environment) !== filter.environment) {
      return false;
    }
    if (filter.status && filter.status !== ALL_FILTER_KEY && ruleSet.status !== filter.status) {
      return false;
    }
    if (
      filter.entityTypeId &&
      filter.entityTypeId !== ALL_FILTER_KEY &&
      ruleSet.dataSource?.entityTypeId !== filter.entityTypeId
    ) {
      return false;
    }
    return true;
  });
};
